import React, { useContext, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useAuth } from "../contexts/AuthContext";
import { validateUser } from "../api/validateUser";
import { TranslationContext } from "../contexts/TranslationContext";
import API_BASE_URL from "../apiConfig";
import LangSwitcher from "../components/LangSwitcher";
import Swal from "sweetalert2";

const Login = () => {
  const { translations, selectedLanguage, setSelectedLanguage, routes } = useContext(TranslationContext);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { login } = useAuth();

  const validationSchema = Yup.object({
    user_name: Yup.string()
      .required(translations.nameReq)
      .min(1, translations.nameReqMin)
      .max(20, translations.nameReqMax),
    password: Yup.string()
      .required(translations.passReq)
      .min(10, translations.passReqMin)
      .max(20, translations.passReqMax),
  });
  const changeLanguage = (language) => {
    setSelectedLanguage(language);
    localStorage.setItem("selectedLanguage", language);
    setIsLangOpen(false);
  };

  const handleLogin = async (values) => {
    setLoading(true);
    setError("");

    const formData = {
      ...values,
      locale: selectedLanguage,
    };

    try {
      const response = await fetch(`${API_BASE_URL}/api/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 422 && data.errors) {
          // Show validation errors
          const errorMessages = Object.values(data.errors).flat().join("\n");
          Swal.fire({
            title: "Validation Error",
            text: errorMessages,
            icon: "error",
            showConfirmButton: true,
          });
          return;
        } else {
          throw new Error(data.message || "An error occurred while logging in");
        }
      }

      const token = data.token;

      if (token) {
        localStorage.setItem("token", token);
        localStorage.setItem("locale", selectedLanguage);
        if (data?.user.locale) {
          changeLanguage(data?.user.locale);
        }
        login(token);
      }
    } catch (err) {
      console.log("err", err);
      setError(err.message || "An error occurred while logging in");

      Swal.fire({
        toast: true,
        position: "top-end",
        title: err.message || "Invalid credentials. Please try again.",
        icon: "error",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <Helmet>
        <title> {translations.logIn} - O.A.V.L.I.T.E</title>
      </Helmet>
      <div className="navbar-header container-fluid px-5 pt-2 mb-5">
        <div className="w-100 emptyBox1"></div>
        <div className="text-center logo-wrapper auth-wrapper pt-3">
          <Link to="/" className="logo-link">
            <img
              src="../images/oavlite-black.png"
              className="img-fluid mx-auto text-center d-block d-logo mb-3"
              alt="oavlite"
            />
          </Link>
          <p className="mb-2 fw-500 tagline__1">
            {translations.taglineV1}
          </p>

        </div>
        <Link to={routes.register} className="btn btn-light bgprimary border-0 rounded-pill oavlite-text-primary">{translations.register}</Link>
        <LangSwitcher />
      </div>

      <div className="login-wrapper text-center position-relative mb-5">
        <div className="container">
          <div className="center-content">
            <h1 className="mb-5">{translations.login}</h1>
            <Formik
              initialValues={{ user_name: "", password: "" }}
              validationSchema={validationSchema}
              onSubmit={handleLogin}
            >
              {({ isSubmitting }) => (
                <Form>
                  <div className="form-group">
                    <label htmlFor="nickname" className="form-col-lable mb-2">{translations.nameLabel}</label>
                    <Field
                      type="text"
                      className="form-control rounded-pill input-field"
                      name="user_name"
                      id="nickname"
                    />
                    <ErrorMessage
                      name="user_name"
                      component="div"
                      className="text-warning mt-1"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="password" className="form-col-lable mb-2">{translations.passwordLabel}</label>
                    <Field
                      type="password"
                      className="form-control rounded-pill input-field"
                      name="password"
                      id="password"
                    />
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="text-warning mt-1"
                    />
                  </div>
                  <div className="form-group col-12">
                    <button
                      type="submit"
                      className="btn btn-light bgprimary border-0 rounded-pill oavlite-text-primary fw-bold theme-btn"
                      disabled={isSubmitting || loading}
                    >
                      {loading ? (
                        <>
                          <i className="ri-loader-fill loader-icon"></i> {translations.logging}
                        </>
                      ) : (
                        translations.logIn
                      )}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
      <footer className="text-center d-footer">
        <p>
          <Link className="color-primary" to={routes.legalNotices}>
            CGU
          </Link>
        </p>
      </footer>
    </>
  );
};

export default Login;
